import { useState } from "react";

import {
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableContainer,
  Typography,
} from "@mui/material";

import { useQuotePriceExchangeRates } from "components/ConvertedPriceTooltip";
import { useHorizontalOverflow, useSentFollowRequests } from "hooks";

import { AddPartnerOutcomeSnackbar } from "../AddCarrierToNetworkButton";
import { ShipmentQuoteTableHeader } from "./components/ShipmentQuoteTableHeader";
import { ShipmentQuoteTableRow } from "./components/ShipmentQuoteTableRow";

import type { AddCarrierAnnouncement } from "../AddCarrierToNetworkButton";
import type { QuoteViewType } from "./types";

interface Props {
  quotes: ShipperCondensedQuote[];
  shipment: ShipperCondensedShipment;
  onQuoteAction: (action: QuoteViewType, quote: ShipperCondensedQuote) => void;
  onSortChange: (column: string, direction: "asc" | "desc") => void;
  sortColumn?: string;
  sortDirection?: "asc" | "desc";
  onRecoveryAction: () => void;
  notificationsByQuote: Map<
    number,
    { count: number; notificationIds: number[] }
  >;
  onQuoteViewed?: (quoteId: number) => void;
}

export function ShipmentQuoteTable(props: Props) {
  const {
    quotes,
    shipment,
    onQuoteAction,
    onSortChange,
    sortColumn,
    sortDirection,
    onRecoveryAction,
    notificationsByQuote,
    onQuoteViewed,
  } = props;

  const { ref, hasHorizontalOverflow } = useHorizontalOverflow();
  // Fetched once for the whole table rather than per row: every price converts
  // at the same latest rate.
  const { cadPerUsd, usdPerCad } = useQuotePriceExchangeRates();
  // Fetched once for the whole table: every row's add-partner link reads the
  // same pending invites, and a row cannot decide whether to offer the action
  // until they have arrived. A table whose carriers are all partners offers the
  // action nowhere, so it stays off the endpoint.
  const { sentFollowRequests } = useSentFollowRequests({
    enabled: quotes.some((quote) => !quote.is_partner_quote),
  });

  // Adding a partner is reported from here rather than from the row: the table
  // outlives any single row's state, so the announcement survives whatever the
  // rows do next. `invitedCarrierIds` keeps every row of the same carrier in
  // step for the rest of the visit — an add that auto-accepts answers its own
  // request, so it never appears in the pending invites above.
  //
  // A carrier is claimed as the request leaves rather than when it lands: a
  // carrier can quote more than once, and until this is set its other rows still
  // offer an add that is already in flight. A failed attempt releases the claim
  // so the shipper can try again.
  const [invitedCarrierIds, setInvitedCarrierIds] = useState<number[]>([]);
  const [announcement, setAnnouncement] =
    useState<AddCarrierAnnouncement | null>(null);

  const handleAddPartnerStarted = (carrierId: number) => {
    setInvitedCarrierIds((current) =>
      current.includes(carrierId) ? current : [...current, carrierId],
    );
  };

  const handleAddPartnerOutcome = (
    carrierId: number,
    outcome: AddCarrierAnnouncement,
  ) => {
    if (outcome.severity === "error") {
      setInvitedCarrierIds((current) =>
        current.filter((invitedId) => invitedId !== carrierId),
      );
    }
    setAnnouncement(outcome);
  };

  if (quotes.length === 0) {
    return (
      <Stack
        mt="1rem"
        direction="column"
        alignContent="center"
        justifyContent="center"
        alignItems="center"
        spacing={1}
      >
        <Typography variant="body1" textAlign="center">
          Filter and sort criteria did not match any quote.
        </Typography>
        <Box>
          <Button variant="outlined" onClick={onRecoveryAction}>
            Clear Filters
          </Button>
        </Box>
      </Stack>
    );
  }

  return (
    <>
      <TableContainer ref={ref} component={Paper}>
        {/* borderCollapse "separate" is required for the sticky ACTIONS cell's
          box-shadow to render; the default "collapse" clips it. */}
        <Table sx={{ borderCollapse: "separate", borderSpacing: 0 }}>
          <ShipmentQuoteTableHeader
            onSortChange={onSortChange}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            hasHorizontalOverflow={hasHorizontalOverflow}
          />
          <TableBody>
            {quotes.map((quote) => (
              <ShipmentQuoteTableRow
                key={quote.quote}
                quote={quote}
                shipment={shipment}
                onQuoteAction={onQuoteAction}
                notificationData={
                  notificationsByQuote.get(quote.quote) ?? {
                    count: 0,
                    notificationIds: [],
                  }
                }
                hasHorizontalOverflow={hasHorizontalOverflow}
                onQuoteViewed={onQuoteViewed}
                cadPerUsd={cadPerUsd}
                usdPerCad={usdPerCad}
                sentFollowRequests={sentFollowRequests ?? null}
                carrierJustInvited={invitedCarrierIds.includes(
                  quote.carrier_id,
                )}
                onAddPartnerStarted={handleAddPartnerStarted}
                onAddPartnerOutcome={handleAddPartnerOutcome}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <AddPartnerOutcomeSnackbar
        announcement={announcement}
        onClose={() => setAnnouncement(null)}
      />
    </>
  );
}
